import * as THREE from "three";
import { asOverheadUI } from "../overheadLayer.js";
import { U } from "../streetScene.js";
import { kaponHeightAt } from "../kaponTerrain.js";
import { dayNight } from "../dayNight.js";
import { HOG_DIRS, type Goblin } from "@ghostland/shared";
import { assetUrl } from "../../assetVer.js";

// Entity sprites load through their OWN LoadingManager so their texture downloads don't report to
// THREE's DefaultLoadingManager — which drives the full-screen boot/scene loading veil. Goblins are
// created from room state right after join (racing the boot reveal), so on the default manager their
// sheet load flashes the "LOADING" veil back up a couple seconds after you've already loaded in.
const ENTITY_MGR = new THREE.LoadingManager();
ENTITY_MGR.setURLModifier((url) => assetUrl(url)); // still cache-bust re-rendered goblin sheets on deploy

/**
 * Renders the server-authoritative wild HOGS (attack NPCs) as billboard sprites in the 3D scene.
 *
 * Each goblin is a base-anchored `THREE.Sprite` paging a per-state sprite sheet (8 directional rows ×
 * N animation frames) under `/assets/mobs/goblin/<state>.png`. Unlike the ambient cats, goblins are NOT
 * autonomous here — position + state + facing all come from the synced `Goblin` schema; this class just
 * mirrors + interpolates them, advances the animation, and floats a "!" alert + health bar overhead.
 *
 * Direction row uses the same heading-relative-to-camera trick as the player billboard, and the sheet
 * rows are packed in `HOG_DIRS` order (row 0 = south/Down), so the visible facing tracks the orbit.
 */

const CELL = 120;
const ROWS = 8;
const LERP = 0.25;                                  // position smoothing toward the authoritative spot
const SCALE = 2.0;                                  // world units tall (120px cell; smaller skinny goblin)
/** Frames per state (matches apps/client/public/assets/mobs/goblin/manifest.json). */
const FRAMES: Record<string, number> = { idle: 9, walk: 6, attack: 11, death: 7 };
/** Per-frame duration (ms) per state. */
const STATE_MS: Record<string, number> = { idle: 150, walk: 110, attack: 75, death: 130 };
/** One-shot states play once and hold the last frame; others loop. */
const ONESHOT: Record<string, boolean> = { attack: true, death: true };
const STATES = ["idle", "walk", "attack", "death"];

interface GoblinEntity {
  sprite: THREE.Sprite;
  texByState: Map<string, THREE.Texture>;          // per-entity cloned textures (independent UV offsets)
  alert: THREE.Sprite;                             // "!" popup
  bar: THREE.Sprite; barCanvas: HTMLCanvasElement; barTex: THREE.CanvasTexture; lastHp: number;
  x: number; z: number;                            // interpolated render position (world units)
  serverX: number; serverZ: number;               // authoritative target (world units)
  dir: string; state: string; health: number; maxHealth: number; alerted: boolean; stunned: boolean;
  phase: number; clock: number; hitClock: number; hitBlue: boolean; // hitClock = flash+shake left; hitBlue = blocked stagger vs damage
}

const GOBLIN_FLASH_MS = 280; // duration of a goblin's got-hit red flash + shake
// The goblin art sits ~15px above the bottom of its 68px cell, so anchor at the FEET (≈0.18 up from the
// cell bottom) instead of the cell bottom — otherwise the empty padding floats it off the ground.
const GOBLIN_FOOT = 0.25;

/** A bold yellow "!" with a dark outline on a transparent canvas, for the aggro alert. */
function makeAlertTexture(): THREE.CanvasTexture {
  const c = document.createElement("canvas"); c.width = 24; c.height = 32;
  const ctx = c.getContext("2d")!;
  ctx.font = "bold 30px system-ui, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.lineWidth = 5; ctx.strokeStyle = "#1c1208"; ctx.strokeText("!", 12, 17);
  ctx.fillStyle = "#ffd23f"; ctx.fillText("!", 12, 17);
  const t = new THREE.CanvasTexture(c); t.magFilter = THREE.NearestFilter; t.minFilter = THREE.LinearFilter; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export class GoblinController {
  private readonly goblins = new Map<string, GoblinEntity>();
  private readonly base = new Map<string, THREE.Texture>(); // state -> loaded sheet
  private readonly alertTex = makeAlertTexture();
  private visible = true;

  constructor(private readonly scene: THREE.Scene) {
    const loader = new THREE.TextureLoader(ENTITY_MGR);
    for (const st of STATES) {
      const t = loader.load(`/assets/mobs/goblin/${st}.png`);
      t.magFilter = t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
      this.base.set(st, t);
    }
  }

  /** A goblin appeared in server state — build its sprite + overlays. */
  add(key: string, goblin: Goblin): void {
    if (this.goblins.has(key)) { this.sync(key, goblin); return; }
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ transparent: true, alphaTest: 0.5 }));
    sprite.center.set(0.5, GOBLIN_FOOT); sprite.scale.set(SCALE, SCALE, 1); sprite.visible = this.visible;
    sprite.position.set(goblin.x * U, 0.02, goblin.y * U);
    this.scene.add(sprite);

    const alert = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.alertTex, transparent: true, depthTest: false }));
    alert.center.set(0.5, 0); alert.scale.set(0.32, 0.42, 1); alert.renderOrder = 1001; alert.visible = false;
    asOverheadUI(alert); // crisp overlay layer — never softened by the world depth-of-field
    this.scene.add(alert);

    const barCanvas = document.createElement("canvas"); barCanvas.width = 40; barCanvas.height = 6;
    const barTex = new THREE.CanvasTexture(barCanvas); barTex.magFilter = THREE.NearestFilter; barTex.minFilter = THREE.NearestFilter;
    const bar = new THREE.Sprite(new THREE.SpriteMaterial({ map: barTex, transparent: true, depthTest: false }));
    bar.center.set(0.5, 0); bar.scale.set(0.7, 0.105, 1); bar.renderOrder = 999; bar.visible = false;
    asOverheadUI(bar); // crisp overlay layer
    this.scene.add(bar);

    const e: GoblinEntity = {
      sprite, texByState: new Map(), alert, bar, barCanvas, barTex, lastHp: -1,
      x: goblin.x * U, z: goblin.y * U, serverX: goblin.x * U, serverZ: goblin.y * U,
      dir: goblin.dir, state: goblin.state, health: goblin.health, maxHealth: goblin.maxHealth, alerted: goblin.alerted,
      stunned: goblin.stunned, phase: 0, clock: 0, hitClock: 0, hitBlue: false,
    };
    this.goblins.set(key, e);
    this.applyState(e, goblin.state);
  }

  /** Mirror the synced schema fields (called on every Goblin onChange). */
  sync(key: string, goblin: Goblin): void {
    const e = this.goblins.get(key); if (!e) return;
    e.serverX = goblin.x * U; e.serverZ = goblin.y * U;
    e.dir = goblin.dir; e.health = goblin.health; e.maxHealth = goblin.maxHealth; e.alerted = goblin.alerted;
    if (goblin.stunned && !e.stunned) { e.hitClock = GOBLIN_FLASH_MS; e.hitBlue = true; } // staggered off a shield → blue shake
    e.stunned = goblin.stunned;
    if (goblin.state !== e.state) this.applyState(e, goblin.state);
  }

  /** Raycast pick a LIVE goblin sprite (for click-to-select as a combat target). Returns its key or null. */
  pick(ray: THREE.Raycaster): string | null {
    const live = [...this.goblins.entries()].filter(([, e]) => e.sprite.visible && e.state !== "death");
    const hits = ray.intersectObjects(live.map(([, e]) => e.sprite), false);
    if (!hits.length) return null;
    const obj = hits[0].object;
    for (const [key, e] of live) if (e.sprite === obj) return key;
    return null;
  }

  /** Live goblins within `range` world units of (wx,wz), nearest first — for Tab targeting. */
  inRange(wx: number, wz: number, range: number): { key: string; x: number; z: number }[] {
    const r2 = range * range, out: { key: string; x: number; z: number }[] = [];
    for (const [key, e] of this.goblins) {
      if (e.state === "death") continue;
      const dx = e.serverX - wx, dz = e.serverZ - wz;
      if (dx * dx + dz * dz <= r2) out.push({ key, x: e.serverX, z: e.serverZ });
    }
    out.sort((a, b) => (a.x - wx) ** 2 + (a.z - wz) ** 2 - ((b.x - wx) ** 2 + (b.z - wz) ** 2));
    return out;
  }

  /** Live render position (world units) of a goblin, or null — for the ground selection ring. */
  posOf(key: string): { x: number; z: number } | null {
    const e = this.goblins.get(key);
    return e ? { x: e.x, z: e.z } : null;
  }

  /** Flash + shake a goblin that just took damage (RED). */
  hit(key: string): void {
    const e = this.goblins.get(key);
    if (e && e.state !== "death") { e.hitClock = GOBLIN_FLASH_MS; e.hitBlue = false; }
  }

  /** Target-frame info for a goblin (health/maxHealth + alive flag), or null if gone. */
  infoOf(key: string): { health: number; maxHealth: number; alive: boolean } | null {
    const e = this.goblins.get(key);
    if (!e) return null;
    return { health: e.health, maxHealth: e.maxHealth, alive: e.state !== "death" };
  }

  /** Nearest DEAD goblin corpse within `range` server px of (px,py) — the skinning target. */
  nearestCorpse(px: number, py: number, range: number): { key: string; x: number; y: number } | null {
    let best = range * range, found: { key: string; x: number; y: number } | null = null;
    for (const [key, e] of this.goblins) {
      if (e.state !== "death") continue;
      const ex = e.serverX / U, ey = e.serverZ / U;
      const dx = ex - px, dy = ey - py, d2 = dx * dx + dy * dy;
      if (d2 <= best) { best = d2; found = { key, x: ex, y: ey }; }
    }
    return found;
  }

  remove(key: string): void {
    const e = this.goblins.get(key); if (!e) return;
    this.scene.remove(e.sprite); (e.sprite.material as THREE.SpriteMaterial).dispose();
    for (const t of e.texByState.values()) t.dispose();
    this.scene.remove(e.alert); (e.alert.material as THREE.SpriteMaterial).dispose();
    this.scene.remove(e.bar); (e.bar.material as THREE.SpriteMaterial).dispose(); e.barTex.dispose();
    this.goblins.delete(key);
  }

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    for (const e of this.goblins.values()) {
      e.sprite.visible = v;
      if (!v) { e.alert.visible = false; e.bar.visible = false; }
    }
  }

  /** Switch the active sheet for a goblin's new state and restart its animation. */
  private applyState(e: GoblinEntity, state: string): void {
    e.state = state; e.phase = 0; e.clock = 0;
    let tex = e.texByState.get(state);
    if (!tex) {
      const baseTex = this.base.get(state); if (!baseTex) return;
      tex = baseTex.clone(); tex.needsUpdate = true;
      tex.repeat.set(1 / FRAMES[state], 1 / ROWS);
      e.texByState.set(state, tex);
    }
    (e.sprite.material as THREE.SpriteMaterial).map = tex;
    (e.sprite.material as THREE.SpriteMaterial).needsUpdate = true;
  }

  private redrawBar(e: GoblinEntity): void {
    const ctx = e.barCanvas.getContext("2d")!;
    const frac = e.maxHealth > 0 ? Math.max(0, Math.min(1, e.health / e.maxHealth)) : 0;
    ctx.clearRect(0, 0, 40, 6);
    ctx.fillStyle = "#1c1208"; ctx.fillRect(0, 0, 40, 6);
    ctx.fillStyle = frac > 0.5 ? "#6ad06a" : frac > 0.25 ? "#e0c34a" : "#d05a4a";
    ctx.fillRect(1, 1, Math.round(38 * frac), 4);
    e.barTex.needsUpdate = true;
  }

  tick(dt: number, camYaw: number, visible: boolean): void {
    this.setVisible(visible);
    if (!visible) return;
    for (const e of this.goblins.values()) {
      // Smooth toward the authoritative position; sit on the Kapon terrain.
      e.x += (e.serverX - e.x) * LERP;
      e.z += (e.serverZ - e.z) * LERP;
      const gy = kaponHeightAt(e.x, e.z);
      // "Got hit" feedback: a decaying red tint + a quick horizontal shake.
      const mat = e.sprite.material as THREE.SpriteMaterial;
      let shake = 0;
      if (e.hitClock > 0) {
        e.hitClock = Math.max(0, e.hitClock - dt * 1000);
        const t = e.hitClock / GOBLIN_FLASH_MS;
        if (e.hitBlue) mat.color.setRGB(1 - 0.7 * t, 1 - 0.38 * t, 1); // blue: its lunge was blocked
        else mat.color.setRGB(1, 1 - 0.72 * t, 1 - 0.72 * t);          // red: took damage
        shake = Math.sin(e.hitClock * 0.07) * 0.14 * t;
        if (e.hitClock === 0) dayNight.lightAt(e.x, e.z, mat.color);
      } else {
        dayNight.lightAt(e.x, e.z, mat.color); // day/night tint + nearby lamps (white when not outdoors)
      }
      e.sprite.position.set(e.x + shake, 0.02 + gy, e.z);

      // Advance the animation (loop, or clamp on one-shot states).
      const n = FRAMES[e.state] ?? 1, ms = STATE_MS[e.state] ?? 140;
      e.clock += dt * 1000;
      while (e.clock >= ms) {
        e.clock -= ms;
        if (ONESHOT[e.state]) e.phase = Math.min(e.phase + 1, n - 1);
        else e.phase = (e.phase + 1) % n;
      }

      // Direction row: goblin's compass dir → heading angle → row relative to the camera.
      const idx = Math.max(0, HOG_DIRS.indexOf(e.dir as (typeof HOG_DIRS)[number]));
      const angle = idx * (Math.PI / 4);
      const row = Math.round(((((angle - camYaw) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)) / (Math.PI / 4)) % ROWS;
      const tex = e.texByState.get(e.state);
      if (tex) tex.offset.set(e.phase / n, 1 - (row + 1) / ROWS);

      // Health bar (shown once damaged, hidden when dead/full).
      const dead = e.state === "death";
      if (e.health !== e.lastHp) { this.redrawBar(e); e.lastHp = e.health; }
      e.bar.visible = !dead && e.health < e.maxHealth;
      if (e.bar.visible) e.bar.position.set(e.x, gy + SCALE * 0.96, e.z);

      // "!" alert popup on aggro.
      e.alert.visible = e.alerted && !dead;
      if (e.alert.visible) e.alert.position.set(e.x, gy + SCALE * 1.04, e.z);
    }
  }

  dispose(): void {
    for (const key of [...this.goblins.keys()]) this.remove(key);
    for (const t of this.base.values()) t.dispose();
    this.alertTex.dispose();
  }
}
